import React, { useState } from "react";
import { Modal, Form, Input, Button, Upload, InputNumber, Divider } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getConfig } from "../../../../config";
import { packCategoryService } from "../../../../services/settings-services";
import { packCategoryActions } from "../../../../store/slices/settings-slices";

const PackCategoryCreateModal = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { cityCode } = getConfig();
  const [loading, setLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const handleImageChange = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setPreviewImage(null);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (uploadedImage) {
        formData.append("file", uploadedImage);
      }
      formData.append(
        "name",
        JSON.stringify({
          en: values.en,
          ru: values.ru,
          ar: values.ar || "",
          ge: values.ge || "",
        })
      );
      formData.append(
        "description",
        JSON.stringify({
          en: values.desc_en || "",
          ru: values.desc_ru || "",
          ar: values.desc_ar || "",
          ge: values.desc_ge || "",
        })
      );
      if (values.proteinMin != null) formData.append("proteinMin", values.proteinMin);
      if (values.proteinMax != null) formData.append("proteinMax", values.proteinMax);
      if (values.fatMin != null) formData.append("fatMin", values.fatMin);
      if (values.fatMax != null) formData.append("fatMax", values.fatMax);
      if (values.carbMin != null) formData.append("carbMin", values.carbMin);
      if (values.carbMax != null) formData.append("carbMax", values.carbMax);

      await packCategoryService.create(formData);
      dispatch(packCategoryActions.changeTrigger());
      toast.success(t("Pack category created successfully"));
      setIsOpen(false);
      form.resetFields();
      setUploadedImage(null);
      setPreviewImage(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error creating pack category"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    form.resetFields();
    setUploadedImage(null);
    setPreviewImage(null);
  };

  return (
    <Modal
      title={t("Create Pack Category")}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
      maskClosable={!loading}
      closable={!loading}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
        <Form.Item label={t("Category Icon")} style={{ marginBottom: "1.5rem" }}>
          <Upload
            listType="picture-card"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleImageChange}
            accept="image/*"
          >
            {previewImage ? (
              <div style={{ position: "relative", width: "100%", height: "100%" }}>
                <img
                  src={previewImage}
                  alt="preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <Button
                  type="text"
                  danger
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    background: "rgba(255, 255, 255, 0.9)",
                  }}
                >
                  ×
                </Button>
              </div>
            ) : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>{t("Upload")}</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          label={`${t("Category Name")} (English)`}
          name="en"
          rules={[{ required: true, message: t("This field is required / Это поле обязательно / ეს ველი აუცილებელია") }]}
        >
          <Input placeholder={t("Enter category name in English")} />
        </Form.Item>

        <Form.Item
          label={`${t("Category Name")} (Русский)`}
          name="ru"
          rules={[{ required: true, message: t("This field is required / Это поле обязательно / ეს ველი აუცილებელია") }]}
        >
          <Input placeholder={t("Enter category name in Russian")} />
        </Form.Item>

        {cityCode === "UAE" ? (
          <Form.Item
            label={`${t("Category Name")} (العربية)`}
            name="ar"
            rules={[{ required: true, message: t("This field is required / Это поле обязательно / ეს ველი აუცილებელია") }]}
          >
            <Input placeholder={t("Enter category name in Arabic")} />
          </Form.Item>
        ) : (
          <Form.Item
            label={`${t("Category Name")} (ქართული)`}
            name="ge"
            rules={[{ required: true, message: t("This field is required / Это поле обязательно / ეს ველი აუცილებელია") }]}
          >
            <Input placeholder={t("Enter category name in Georgian")} />
          </Form.Item>
        )}

        <Divider orientation="left" style={{ marginTop: "1.5rem" }}>{t("Description")}</Divider>

        <Form.Item label={`${t("Description")} (English)`} name="desc_en">
          <Input.TextArea rows={2} placeholder={t("Enter description in English")} />
        </Form.Item>
        <Form.Item label={`${t("Description")} (Русский)`} name="desc_ru">
          <Input.TextArea rows={2} placeholder={t("Enter description in Russian")} />
        </Form.Item>
        {cityCode === "UAE" ? (
          <Form.Item label={`${t("Description")} (العربية)`} name="desc_ar">
            <Input.TextArea rows={2} placeholder={t("Enter description in Arabic")} />
          </Form.Item>
        ) : (
          <Form.Item label={`${t("Description")} (ქართული)`} name="desc_ge">
            <Input.TextArea rows={2} placeholder={t("Enter description in Georgian")} />
          </Form.Item>
        )}

        <Divider orientation="left">{t("Macros Range (%)")}</Divider>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1rem" }}>
          <Form.Item label={`${t("Protein")} Min %`} name="proteinMin">
            <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="0" />
          </Form.Item>
          <Form.Item label={`${t("Protein")} Max %`} name="proteinMax">
            <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="100" />
          </Form.Item>
          <Form.Item label={`${t("Fat")} Min %`} name="fatMin">
            <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="0" />
          </Form.Item>
          <Form.Item label={`${t("Fat")} Max %`} name="fatMax">
            <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="100" />
          </Form.Item>
          <Form.Item label={`${t("Carbs")} Min %`} name="carbMin">
            <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="0" />
          </Form.Item>
          <Form.Item label={`${t("Carbs")} Max %`} name="carbMax">
            <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="100" />
          </Form.Item>
        </div>

        <Form.Item style={{ marginBottom: 0, marginTop: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button onClick={handleCancel} disabled={loading}>{t("Cancel")}</Button>
            <Button type="primary" htmlType="submit" loading={loading} disabled={loading}>{t("Create")}</Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export { PackCategoryCreateModal };
